import moment from 'moment';
import {
  expenseInputTypes,
  LOST_RECEIPT_OPTION,
  customFieldDataTypes,
  EXPENSE_ACTION_TYPES,
  EXPENSE_FORM_KEYS,
  EXPENSE_MAPPED_INFO_FIELD_KEYS,
} from '../constants/Enums';
import {
  DynamoDBFieldNames,
  ExpenseCategories,
  DEFAULT_SELECT_SUBCATEGORY,
  REPORT_TOGGLE_KEY,
  REPORT_TOGGLE_OPTIONS,
  TOGGLE_KEYS,
  MileageCategories,
} from '../constants/ExpenseCategories';
import { getPaymentTypeText } from '../store/actions/expense';
import { isNullOrEmpty } from './common.utils';
import { Platform } from 'react-native';
const { DATE_TIME } = customFieldDataTypes;

const DATE_FORMATS = [
  'DD/MM/YYYY',
  'MM/DD/YYYY',
  'YYYY-MM-DD',
  'DD-MM-YYYY',
  'MM-DD-YYYY',
  'MMMM DD YYYY',
  'MMM DD YYYY',
  'DD MMMM YYYY',
  'DD MMM YYYY',
  'MMMM DD, YYYY',
  'MMM DD, YYYY',
  'DD MMMM, YYYY',
  'DD MMM, YYYY',
  'Do MMM YYYY',
  'Do MMMM YYYY',
  'MMMM Do YYYY',
  'MMM Do YYYY',
];

export const extractFormFieldDefaults = (subCategory) => {
  const uniqueFields = {}; // Object to store key-value pairs for the subcategory form fields

  for (const step of Object.values(subCategory.formFields)) {
    // Iterate over each step in formFields
    for (const field of step) {
      // Iterate over the fields in each step
      const { stateName, currencyStateName, inputConfig } = field || {};
      const { inputType, options } = inputConfig || {};

      switch (inputType) {
        case expenseInputTypes.MONEY:
          if (stateName === DynamoDBFieldNames.RATE) {
            uniqueFields[stateName] = '';
            uniqueFields[currencyStateName] = 'Mi';
          } else {
            uniqueFields[stateName] = '';
            uniqueFields[currencyStateName] = 'USD';
          }
          break;
        case expenseInputTypes.PULL_DOWN:
          uniqueFields[stateName] = options?.[0];
          break;
        case expenseInputTypes.PAYMENT_TYPE:
          uniqueFields[stateName] = options?.[0];
          break;
        default:
          uniqueFields[stateName] = '';
      }
    }
  }

  return { ...uniqueFields, manuallyUpdatedFields: [] };
};

// Utility function to initialize all unique stateNames
export const initializeExpenseState = (categories) => {
  const uniqueFields = {}; // Object to track stateNames and their initial values

  for (const category of categories) {
    for (const subCategory of category.SubCategories) {
      if (isNullOrEmpty(subCategory?.formFields)) {
        continue;
      }
      // Merge the result of processing the subcategory form fields
      Object.assign(uniqueFields, extractFormFieldDefaults(subCategory));
    }
  }

  return {
    ...TOGGLE_KEYS.reduce((acc, { key }) => {
      return { ...acc, [key]: false };
    }, {}),
    [REPORT_TOGGLE_KEY]: REPORT_TOGGLE_OPTIONS.NEW_REPORT.label,
    ...uniqueFields,
    receipts: [],
    // Initialize manuallyUpdatedFields, which will be used to track fields that have been manually updated
    manuallyUpdatedFields: [],
  };
};

export const hasIncompleteMandatoryFields = (expense) => {
  const expenseSubCategory = getSubcategoryById(
    expense.ExpenseSubCategory,
    isMileageExpense(expense) ? MileageCategories : ExpenseCategories
  );

  const validateFieldsByStep = (step) => {
    return expenseSubCategory.formFields[step].some((field) => {
      const isMandatory = field.mandatory[EXPENSE_ACTION_TYPES.UPDATE];
      if (isMandatory) {
        const stateName = field.stateName;
        const fieldValue = expense[stateName];
        const format = field.inputConfig.format;
        if (stateName === DynamoDBFieldNames.PAYMENT_TYPE) {
          if (!isNullOrEmpty(fieldValue)) {
            return false;
          }
          // if PaymentType is not set then check if it's a charge-based expense
          return isNullOrEmpty(expense?.Charge);
        }

        if (isNullOrEmpty(fieldValue)) {
          return true;
        }

        if (format === DATE_TIME) {
          return fieldValue === '0001-01-01T00:00:00';
        }
      }
      return false;
    });
  };

  return (
    validateFieldsByStep(EXPENSE_FORM_KEYS.STEP_ONE) ||
    validateFieldsByStep(EXPENSE_FORM_KEYS.STEP_TWO)
  );
};

export const formatDate = (input) => {
  const dateFormat = 'YYYY-MM-DD';
  if (isNullOrEmpty(input)) {
    return '';
  }
  const cleanedDateString = input.replace(/(\d+)(st|nd|rd|th)/, '$1');
  // Try parsing without format
  const date = moment(cleanedDateString);
  if (date.isValid()) {
    return date.format(dateFormat);
  }

  // Try each format
  for (const format of DATE_FORMATS) {
    const date = moment(cleanedDateString, format, true);
    if (date.isValid()) {
      return date.format(dateFormat);
    }
  }

  return '';
};
// To add a new field, include its key from the charge object and its corresponding
// DynamoDB field name in the `fieldMappings` object.
export const initializeBusinessChargeValues = (charge) => {
  const lockedFields = [];
  const chargeFieldsMap = {};

  // Mapping of charge keys to DynamoDB field names
  const straightFieldMappings = {
    Merchant: DynamoDBFieldNames.MERCHANT,
    ChargeAmount: DynamoDBFieldNames.AMOUNT,
    Currency: DynamoDBFieldNames.CURRENCY,
    ChargeDate: DynamoDBFieldNames.CHARGE_DATE,
    Location: DynamoDBFieldNames.LOCATION,
    CreditCardKey: DynamoDBFieldNames.PAYMENT_TYPE,
  };

  // Iterate over the straight mapping and process each field
  Object.entries(straightFieldMappings).forEach(([key, fieldName]) => {
    if (!isNullOrEmpty(charge?.[key])) {
      chargeFieldsMap[fieldName] = charge[key];
      lockedFields.push(fieldName);
    } else {
      chargeFieldsMap[fieldName] = '';
    }
  });

  // Special case for Payment Type
  if (!isNullOrEmpty(charge?.CreditCardKey) && !isNullOrEmpty(charge?.CreditCardDescription)) {
    chargeFieldsMap[DynamoDBFieldNames.PAYMENT_TYPE] = {
      text: charge.CreditCardDescription,
      value: charge.CreditCardKey,
    };
    lockedFields.push(DynamoDBFieldNames.PAYMENT_TYPE);
  }

  return { chargeFieldsMap, lockedFields };
};

export const getSubcategoryById = (subCategoryId, expenseCategories = ExpenseCategories) => {
  if (isNullOrEmpty(subCategoryId)) {
    return DEFAULT_SELECT_SUBCATEGORY;
  }

  for (const category of expenseCategories) {
    // Check each category's Categories array for matching CategoryId
    for (const subCategory of category.SubCategories) {
      if (subCategory.SubCategoryId === subCategoryId) {
        // If CategoryId matches, return groupKey or default 1
        return subCategory;
      }
    }
  }
  return DEFAULT_SELECT_SUBCATEGORY;
};

const FIELD_TRANSLATION_HANDLERS = {
  [DynamoDBFieldNames.PAYMENT_TYPE]: (value, creditCards) => {
    // it means that the value is already in the correct format (coming from a charge)
    if (value?.text && value?.value) {
      return value;
    }
    // TODO: remove the following condition when we ensure that all credit cards are in the same format
    if (value === 'BC') {
      return {
        text: 'Business Card',
        value,
      };
    }
    const allCards = [...creditCards.corporate, ...creditCards.personal, ...creditCards.cash];
    const selectedCard = allCards.find((card) => card.Value == value);
    if (isNullOrEmpty(selectedCard)) {
      return '--Select--';
    }
    const cardObj = {
      text: selectedCard?.Text,
      value: selectedCard?.Value,
    };
    return cardObj;
  },
  [DynamoDBFieldNames.CURRENCY]: (value) => value,
  [DynamoDBFieldNames.AMOUNT]: (value) => {
    // Ensure two decimal places
    return parseFloat(value).toFixed(2);
  },
  [DynamoDBFieldNames.DISTANCE]: (value) => {
    // Ensure two decimal places
    return parseFloat(value).toFixed(2);
  },
  [DynamoDBFieldNames.RATE]: (value) => {
    // Ensure two decimal places
    return parseFloat(value).toFixed(2);
  },
};
// Initially designed to handle translations for dropdown fields, but can be extended for other field types
export const applyFieldTranslations = (expense, creditCards) => {
  const translatedExpense = { ...expense };

  for (const field in FIELD_TRANSLATION_HANDLERS) {
    if (expense[field]) {
      const translateFunction = FIELD_TRANSLATION_HANDLERS[field];
      translatedExpense[field] = translateFunction(expense[field], creditCards);
    }
  }

  return translatedExpense;
};

export const applyReceiptsTranslation = (Receipts) => {
  if (isNullOrEmpty(Receipts)) {
    return [LOST_RECEIPT_OPTION];
  }
  return Receipts?.map((receipt, index) => {
    return { ...receipt, id: index + 1 };
  });
};

export const shouldUpdateExpenseState = (isCreationForm, selectedCharge) => {
  if (isCreationForm) {
    return isNullOrEmpty(selectedCharge);
  }
  return false;
};

const shouldUpdateComparison = (currentField, comparisonField) => {
  // Check if the current field's confidence is greater than 0
  if (currentField.confidence <= 0) {
    return false;
  }

  // Check if the current field's value is not equal to an empty string
  if (currentField.value === '') {
    return false;
  }

  // Check if there is no comparison field or if the current field's confidence is greater than the comparison field's confidence
  if (comparisonField && currentField.confidence <= comparisonField.confidence) {
    return false;
  }

  return true;
};

const getMappedFields = (item) => {
  const receiptsVaultMappedFields = item?.[EXPENSE_MAPPED_INFO_FIELD_KEYS.RECEIPTS_VAULT];
  if (!isNullOrEmpty(receiptsVaultMappedFields)) {
    return receiptsVaultMappedFields;
  }
  const uploadedReceiptMappedFields = item?.[EXPENSE_MAPPED_INFO_FIELD_KEYS.UPLOADED_RECEIPT];
  if (!isNullOrEmpty(uploadedReceiptMappedFields)) {
    return uploadedReceiptMappedFields;
  }
  return {};
};

const getHighestConfidenceData = (receipts) => {
  const comparison = {}; // Object to store the highest confidence values
  const result = {};
  // Iterate over each object in the array
  for (const item of receipts) {
    const mappedFields = getMappedFields(item); // Get the mappedInfoFields property
    for (const key of Object.keys(mappedFields)) {
      if (shouldUpdateComparison(mappedFields[key], comparison[key])) {
        comparison[key] = mappedFields[key];
        result[key] = mappedFields[key].value;
      }
    }
  }
  return result;
};

export const getRefinedAndHighestConfidenceData = (receipts) => {
  const result = getHighestConfidenceData(receipts);
  // Only include CHARGE_DATE if ExpenseDate exists and is valid

  const formattedChargeDate = formatDate(result.ExpenseDate);
  if (formattedChargeDate) {
    const oneYearBack = moment().subtract(1, 'year');
    const oneYearForward = moment().add(1, 'year');
    const chargeDate = moment(formattedChargeDate);

    if (chargeDate.isBetween(oneYearBack, oneYearForward, 'day', '[]')) {
      result[DynamoDBFieldNames.CHARGE_DATE] = formattedChargeDate;
    }
  }
  if (!isNullOrEmpty(result.Amount)) {
    result.Amount = parseFloat(result.Amount).toFixed(2);
  }
  // Add extra conditions here to refine the desired data
  return result;
};

export const getRangeDate = (item, momentFormat, label) => {
  const chargeDates = item?.Expenses?.map((expense) => expense.ChargeDate).filter(Boolean);
  const earliestChargeDate =
    chargeDates.length > 0
      ? moment.min(chargeDates.map((date) => moment(date))).format(momentFormat)
      : '';
  const latestChargeDate =
    chargeDates.length > 0
      ? moment.max(chargeDates.map((date) => moment(date))).format(momentFormat)
      : '';
  const rangeDate =
    earliestChargeDate && latestChargeDate
      ? `${earliestChargeDate} ${label} ${latestChargeDate}`
      : '';
  return rangeDate;
};

export const filterManuallyUpdatedFields = (data, manuallyUpdatedFields) => {
  if (!data || typeof data !== 'object') {
    return {};
  }

  if (isNullOrEmpty(manuallyUpdatedFields)) {
    return data;
  }

  return Object.keys(data).reduce((acc, key) => {
    if (!manuallyUpdatedFields.includes(key)) {
      acc[key] = data[key];
    }
    return acc;
  }, {});
};

export const extractPaymentSuffix = (text) => {
  if (isNullOrEmpty(text) || text === '--Select--' || typeof text !== 'string') {
    return text || '';
  }
  const parts = text?.split('-');
  return parts.length > 1 ? `- ${parts[parts.length - 1].trim()}` : text;
};

export const appendReportNumber = (text, reportNumber) => {
  return `${text} - ${reportNumber}`;
};

export const isMileageExpense = (item) => {
  return item.ExpenseSubCategory === 15;
};

export const isIPhoneBrowser = () => {
  return Platform.OS === 'web' && /iPhone|iPad|iPod/.test(navigator.userAgent);
};
